'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import MobilePartnerLayout from '@/components/MobilePartnerLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Kavling {
  id: string;
  name: string;
  status: string;
  price: number | null;
  area_m2: number | null;
  image_url: string | null;
  image_alt: string | null;
  promo_label: string | null;
  project: string;
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'AVAILABLE': return { label: 'Tersedia', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' };
    case 'HOLD': return { label: 'Hold', cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' };
    case 'BOOKED': return { label: 'Dibooking', cls: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' };
    case 'SOLD': return { label: 'Terjual', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
    default: return { label: status, cls: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
  }
}

function formatPrice(price: number | null): string {
  if (!price) return '-';
  if (price >= 1_000_000_000) return `Rp ${(price / 1_000_000_000).toFixed(1)} M`;
  return `Rp ${(price / 1_000_000).toFixed(0)} Juta`;
}

interface ShareSheetProps {
  kavling: Kavling;
  agentCode: string;
  onClose: () => void;
}

function ShareSheet({ kavling, agentCode, onClose }: ShareSheetProps) {
  const [copied, setCopied] = useState(false);
  const refUrl = `https://betaland.id/kavling/${kavling.id}?ref=${agentCode}`;
  const waText = encodeURIComponent(
    `Halo! Ada kavling menarik di ${kavling.project} - ${kavling.name}, luas ${kavling.area_m2 || '-'} m², harga ${formatPrice(kavling.price)}. Cek di sini: ${refUrl}`
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(refUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-t-3xl shadow-2xl pb-8">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <span className="text-base font-bold text-foreground">Bagikan Kavling</span>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <div className="px-5 pt-4">
          {/* Kavling preview */}
          <div className="flex gap-3 bg-muted rounded-xl p-3 mb-4">
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-border flex-shrink-0">
              <img
                src={kavling.image_url || '/assets/images/no_image.png'}
                alt={kavling.image_alt || kavling.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
              />
            </div>
            <div>
              <p className="font-bold text-sm text-foreground">{kavling.project}</p>
              <p className="text-xs text-muted-foreground">{kavling.name}</p>
              <p className="text-xs font-semibold text-primary mt-0.5">{formatPrice(kavling.price)}</p>
            </div>
          </div>

          {/* Share options */}
          <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Bagikan Melalui</p>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <a
              href={`https://wa.me/?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5"
            >
              <div className="w-12 h-12 rounded-2xl bg-green-500 flex items-center justify-center">
                <Icon name="ChatBubbleLeftRightIcon" size={22} className="text-white" />
              </div>
              <span className="text-xs text-muted-foreground">WhatsApp</span>
            </a>
            <button onClick={handleCopy} className="flex flex-col items-center gap-1.5">
              <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
                <Icon name={copied ? 'CheckIcon' : 'LinkIcon'} size={22} className={copied ? 'text-green-600' : 'text-foreground'} />
              </div>
              <span className="text-xs text-muted-foreground">{copied ? 'Tersalin!' : 'Salin Link'}</span>
            </button>
            <button className="flex flex-col items-center gap-1.5">
              <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
                <Icon name="QrCodeIcon" size={22} className="text-foreground" />
              </div>
              <span className="text-xs text-muted-foreground">QR Code</span>
            </button>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(refUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5"
            >
              <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center">
                <Icon name="PaperAirplaneIcon" size={22} className="text-white" />
              </div>
              <span className="text-xs text-muted-foreground">Telegram</span>
            </a>
          </div>

          {/* Ref URL */}
          <div className="bg-muted rounded-xl px-4 py-3 flex items-center gap-2">
            <p className="text-xs text-muted-foreground flex-1 truncate font-mono">{refUrl}</p>
            <button onClick={handleCopy}>
              <Icon name="ClipboardDocumentIcon" size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MobileKavlingPage() {
  const { user, profile } = useAuth();
  const [kavlings, setKavlings] = useState<Kavling[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('Semua');
  const [shareItem, setShareItem] = useState<Kavling | null>(null);

  const agentCode = profile?.agent_code || 'AGT-001';

  const supabase = createClient();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Query from master 'plots' table joined with 'projects' (same source as public listing & admin)
      const { data, error } = await supabase
        .from('plots')
        .select(`
          id,
          plot_number,
          block,
          area_m2,
          price,
          status,
          image_url,
          image_alt,
          promo_label,
          promo,
          created_at,
          projects!inner (
            id,
            name,
            status
          )
        `)
        .eq('projects.status', 'ACTIVE')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped: Kavling[] = data.map((plot: any) => {
          const proj = plot.projects;
          const plotName = plot.block
            ? `Kavling ${plot.block}-${plot.plot_number}`
            : `Kavling ${plot.plot_number}`;
          // Normalize DP_PAID → BOOKED for display
          let displayStatus = plot.status;
          if (plot.status === 'DP_PAID') displayStatus = 'BOOKED';
          return {
            id: plot.id,
            name: plotName,
            status: displayStatus,
            price: plot.price ?? null,
            area_m2: plot.area_m2 ?? null,
            image_url: plot.image_url ?? null,
            image_alt: plot.image_alt ?? plotName,
            promo_label: plot.promo_label ?? null,
            project: proj?.name ?? '-',
          };
        });
        setKavlings(mapped);
      }
      setLoading(false);
    };
    fetchData();

    // Real-time sync when plots or projects change
    const channel = supabase
      .channel('partner_kavling_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'plots' }, fetchData)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'projects' }, fetchData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const tabs = ['Semua', 'Tersedia', 'Promo'];
  const filtered = kavlings.filter((k) => {
    const matchSearch = k.name.toLowerCase().includes(search.toLowerCase()) ||
      (k.project || '').toLowerCase().includes(search.toLowerCase());
    const matchTab = filterTab === 'Semua' ? true :
      filterTab === 'Tersedia' ? k.status === 'AVAILABLE' :
      filterTab === 'Promo' ? !!k.promo_label : true;
    return matchSearch && matchTab;
  });

  return (
    <MobilePartnerLayout showFloatingButton={false}>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 pt-12 pb-3 sticky top-0 z-30">
        <h1 className="text-lg font-bold text-foreground mb-3">Kavling</h1>
        {/* Search */}
        <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5 mb-3">
          <Icon name="MagnifyingGlassIcon" size={18} className="text-muted-foreground flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari kavling atau project..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')}>
              <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
            </button>
          )}
        </div>
        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setFilterTab(tab)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                filterTab === tab ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Kavling list */}
      <div className="px-4 py-4 space-y-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden animate-pulse">
              <div className="h-44 bg-muted" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
                <div className="h-8 bg-muted rounded mt-3" />
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="MapIcon" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Tidak ada kavling ditemukan</p>
          </div>
        ) : (
          filtered.map((kavling) => {
            const badge = getStatusBadge(kavling.status);
            return (
              <div key={kavling.id} className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
                {/* Image */}
                <div className="relative h-44">
                  <img
                    src={kavling.image_url || '/assets/images/no_image.png'}
                    alt={kavling.image_alt || kavling.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                  />
                  {kavling.promo_label && (
                    <span className="absolute top-3 left-3 bg-orange text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      {kavling.promo_label}
                    </span>
                  )}
                  <span className={`absolute top-3 right-3 flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                    {badge.label}
                  </span>
                </div>
                {/* Info */}
                <div className="p-4">
                  <p className="font-bold text-foreground text-base leading-tight">{kavling.project}</p>
                  <p className="text-sm text-muted-foreground">{kavling.name}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    {kavling.area_m2 && <span>{kavling.area_m2} m²</span>}
                    <span className="font-bold text-primary text-sm">{formatPrice(kavling.price)}</span>
                  </div>
                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Halo! Saya ingin info kavling ${kavling.name} di ${kavling.project}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold active:scale-95 transition-all"
                    >
                      <Icon name="ChatBubbleLeftRightIcon" size={16} />
                      WhatsApp
                    </a>
                    <button
                      onClick={() => setShareItem(kavling)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary/10 text-primary rounded-xl text-sm font-semibold active:scale-95 transition-all"
                    >
                      <Icon name="ShareIcon" size={16} />
                      Share
                    </button>
                    <Link
                      href={`/partner/mobile/kavling/${kavling.id}`}
                      className="px-3 py-2.5 bg-muted text-foreground rounded-xl text-sm font-semibold active:scale-95 transition-all"
                    >
                      <Icon name="ChevronRightIcon" size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {shareItem && (
        <ShareSheet
          kavling={shareItem}
          agentCode={agentCode}
          onClose={() => setShareItem(null)}
        />
      )}
    </MobilePartnerLayout>
  );
}
